import React, { useState, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown } from 'lucide-react';
export type Language = 'EN' | 'BM' | 'ZH';
const LanguageContext = createContext<Language>('EN');
export const useLanguage = () => useContext(LanguageContext);
interface SplitLayoutProps {
  children: React.ReactNode;
  imageSrc?: string;
  logoSrc?: string;
  title?: string;
  description?: string;
}
export function SplitLayout({
  children,
  imageSrc = 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?q=80&w=2000&auto=format&fit=crop',
  logoSrc = 'https://cdn.magicpatterns.com/uploads/jf93DJKVVHMQcEr8jhkoLt/maybank-logo-png-transparent.png'
}: SplitLayoutProps) {
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [lang, setLang] = useState<Language>('EN');
  const langLabel = lang === 'EN' ? 'EN' : lang === 'BM' ? 'BM' : '中文';
  return <LanguageContext.Provider value={lang} data-id="element-908">
      <div className="flex min-h-screen w-full bg-white overflow-hidden" data-id="element-909">
        {/* Left Content Side */}
        <div className="w-full lg:w-1/2 flex flex-col h-screen overflow-y-auto" data-id="element-910">
          <div className="flex-1 flex flex-col px-6 py-8 sm:px-12 lg:px-16 xl:px-24 max-w-2xl mx-auto w-full" data-id="element-911">
            {/* Header */}
            <div className="mb-12 flex items-center justify-between" data-id="element-912">
              <img src={logoSrc} alt="Maybank" className="h-10 w-auto object-contain" data-id="element-913" />

              {/* Language Selector */}
              <div className="relative" data-id="element-914">
                <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFCC00]" data-id="element-915">
                  <Globe className="w-4 h-4 text-gray-700" data-id="element-916" />
                  <span className="text-sm font-semibold text-gray-900" data-id="element-917">
                    {langLabel}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-918" />
                </button>

                {isLangOpen && <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50" data-id="element-919">
                    <button onClick={() => {
                  setLang('EN');
                  setIsLangOpen(false);
                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900" data-id="element-920">
                      English
                    </button>
                    <button onClick={() => {
                  setLang('BM');
                  setIsLangOpen(false);
                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900" data-id="element-921">
                      Bahasa Melayu
                    </button>
                    <button onClick={() => {
                  setLang('ZH');
                  setIsLangOpen(false);
                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900" data-id="element-922">
                      中文 (Simplified)
                    </button>
                  </div>}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col justify-center" data-id="element-923">
              <AnimatePresence mode="wait" data-id="element-924">
                <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="w-full" data-id="element-925">
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="mt-12 text-xs text-gray-400" data-id="element-926">
              <p data-id="element-927">
                &copy; {new Date().getFullYear()} Maybank. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* Right Image Side */}
        <div className="hidden lg:block lg:w-1/2 relative bg-gray-900" data-id="element-928">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10" data-id="element-929" />
          <img src={imageSrc} alt="Business owner managing operations" className="absolute inset-0 h-full w-full object-cover" data-id="element-930" />
          <div className="absolute bottom-0 left-0 right-0 p-12 z-20 text-white" data-id="element-931">
            <blockquote className="max-w-lg" data-id="element-932">
              <p className="text-xl font-medium mb-4" data-id="element-933">
                "Maybank has been instrumental in growing our e-commerce
                business across Malaysia. Their digital banking platform is
                truly world-class."
              </p>
              <footer className="flex items-center space-x-4" data-id="element-934">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm" data-id="element-935">
                  LT
                </div>
                <div data-id="element-936">
                  <div className="font-semibold" data-id="element-937">Lee Tan</div>
                  <div className="text-white/70 text-sm" data-id="element-938">
                    CEO, TechMart Sdn Bhd
                  </div>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </div>
    </LanguageContext.Provider>;
}